import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import _ from "lodash";

import DOMPurify from 'dompurify';
import Parser from 'html-react-parser';
import { ThreeDots } from "react-loader-spinner";
import { confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";

import Loading from "../../common/Loading";
import { Button, TextInputField } from "../../helpers";
import Menu from "../../helpers/Menu";
import DocumentView from "../../common/DocumentView";

import { approveAdjustmentNote, cancelAdjustmentNotes, getAdjustmentNoteById, getSellerDebitNoteDocument, updateAdjustmentNote } from "../../services/api/adjustmentNotes";
import { getSellerPOBySellerPOId } from "../../services/api/sellerPO";
import { getSellerPIPunchById } from "../../services/api/sellerPIPunch";
import { getMaterialMasterAndTempMaterialIds } from "../../services/api/materialMaster";
import { getDocCopyByDocNo } from "../../services/api/docCopy";
import { raiseToast } from "../../services/redux/reducers/toast/toast";
import downloadFile from "../../utils/DownloadFile";
import { uploadFile } from "../../services/api/company";
import { getStockMovesByDocNos } from "../../services/api/stockMoves";


const DebitNote = () => {
    const { adjustmentNoteId } = useParams()

    const [isUploadFileVisible, setIsUploadFileVisible] = useState(false)
    const [adjustmentNote, setAdjustmentNoteDetails] = useState({})
    const [adjustmentNoteItems, setAdjustmentNoteItems] = useState([])
    const [sellerPIPunchItems, setSellerPIPunchItems] = useState([])
    const [sellerPOItems, setSellerPOItems] = useState([])
    const [sellerDebitNoteHTML, setSellerDebitNoteHTML] = useState("")
    const [SDNStockMoves, setSDNStockMoves] = useState([])
    const [adjustmentNotePDFURL, setAdjustmentNotePDFURL] = useState(null)
    const [creditNoteDocFile, setCreditNoteDocFile] = useState(null)
    const [creditNoteNo, setCreditNoteNo] = useState("")
    const [isLoading, setIsLoading] = useState(true)
    const [isPDFLoading, setIsPDFLoading] = useState(false)
    const [isPageLoading, setIsPageLoading] = useState(false)

    const actionMenu = useRef(null)

    const dispatch = useDispatch()

    useEffect(() => {
        getAdjustmentDetails()
    }, [])

    const actionMenuItems = [
        {
            label: "Approve",
            visible: adjustmentNote?.statusId === 7,  //Add this when debit note is creating
            command: () => {
                handleApproveAdjustmentNote()
            },
            permissions: [45], //check this
            disabled: isLoading || isPDFLoading || isPageLoading
        },
        {
            label: isPDFLoading ? "Loading..." : 'Download Debit Note',
            command: () => {
                // handleCancelClick()
                setIsPDFLoading(true)
                getSellerDebitNotePDFURL()
            },
            visible: adjustmentNote?.statusId !== 7,
            disabled: isLoading || isPDFLoading || isPageLoading
        },
        {
            label: adjustmentNote?.docProof ? "View Credit Note Doc" : "Upload Credit Note Doc",
            command: () => handleCreditNoteDoc(),
            visible: adjustmentNote?.statusId !== 7,
            disabled: adjustmentNote.statusId === 6 || isLoading || isPDFLoading || isPageLoading
        },
        {
            label: 'Cancel Debit Note',
            command: () => {
                handleCancelAdjustmentNote()
            },
            className: "cancel-btn",
            disabled: adjustmentNote.statusId === 6 || isLoading || isPDFLoading || isPageLoading
        },
        {
            label: "Regenerate Document",
            className: "",
            command: () => regenerateDocCopy(),            
            permissions: [102],
            visible: true,
            disabled: isLoading || isPDFLoading || isPageLoading
        }
    ]
    const regenerateDocCopy = async () => {
        if(!window.confirm("Confirm to regenerate document")){
            return
        }

        setIsLoading(true)

        const createNewDocCopy = true
        const docCopyRes = await getDocCopyByDocNo(adjustmentNote.docNo, null, createNewDocCopy)

        if(docCopyRes.failure){
            dispatch(raiseToast({severity:'error', detail: docCopyRes.data.error, life: 3000}))
        }else{
            setAdjustmentNotePDFURL(docCopyRes?.docLocation)
        }

        setIsLoading(false)
    }

    const getAdjustmentDetails = async () => {
        const adjustmentDetails = await getAdjustmentNoteById(adjustmentNoteId)
        if(adjustmentDetails.failure){
            return dispatch(raiseToast({severity:'error', summary: 'Adjustment note details', detail: adjustmentDetails.data.error, life: 3000}))
        }
        setAdjustmentNoteDetails(adjustmentDetails?.data?.adjustmentNotes)
        setAdjustmentNoteItems(adjustmentDetails.data.adjustmentNoteItems)

        if(adjustmentDetails?.data?.adjustmentNotes?.sellerPIPunchId){

            const sellerDebitNoteDocRes = await getSellerDebitNoteDocument(adjustmentNoteId)
            if(sellerDebitNoteDocRes.failure){
                return dispatch(raiseToast({severity:'error', summary: 'Document', detail: sellerDebitNoteDocRes.data.error, life: 3000}))
            }

            if(sellerDebitNoteDocRes.data.docCopy){
                setAdjustmentNotePDFURL(sellerDebitNoteDocRes.data.docCopy.docLocation)
            } else {
                setSellerDebitNoteHTML(sellerDebitNoteDocRes.data.documentHTML)
            }

            const sellerPIPunchRes = await getSellerPIPunchById(adjustmentDetails?.data?.adjustmentNotes?.sellerPIPunchId)
            if(sellerPIPunchRes.failure){
                return dispatch(raiseToast({severity:'error', summary: 'PI Punch details', detail: sellerPIPunchRes.data.error, life: 3000}))
            }

            const sellerPORes = await getSellerPOBySellerPOId(sellerPIPunchRes?.data?.sellerPOId, true)
            if(sellerPORes.failure){
                return dispatch(raiseToast({severity:'error', summary: 'Seller PO details', detail: sellerPORes.data.error, life: 3000}))
            }

            const materialIds = sellerPORes?.data?.sellerPIPunchItems.map(eachPIPunchItem => eachPIPunchItem.materialId)
            const materialRes = await getMaterialMasterAndTempMaterialIds({ materialIds: materialIds })
            if(materialRes.failure){
                return dispatch(raiseToast({severity:'error', summary: 'Material details', detail: materialRes.data.error, life: 3000}))
            }

            const sellerDebitNoteStockMove = await getStockMovesByDocNos({docNos: [adjustmentDetails?.data?.adjustmentNotes?.docNo]})
            if(sellerDebitNoteStockMove.failure){
                return dispatch(raiseToast({severity:'error', summary: 'SDN stock move', detail: sellerDebitNoteStockMove.data.error, life: 3000}))
            }

            setSellerPOItems(sellerPORes.data.sellerPOItems)
            setSellerPIPunchItems(sellerPORes.data.sellerPIPunchItems)
            setSDNStockMoves(sellerDebitNoteStockMove.data)

            setIsLoading(false)
        }
    }

    const getSellerDebitNotePDFURL = async () => {
        const docCopyRes = await getDocCopyByDocNo(adjustmentNote.docNo, true)
        setIsPageLoading(true)

        if(docCopyRes.failure){
            setIsPDFLoading(false)
            setIsPageLoading(false)
            return dispatch(raiseToast({severity:'error', summary: "Seller Debit Note", detail:docCopyRes.data.error, life: 3000}))
        }
        
        if(!docCopyRes?.docLocation){
            setIsPDFLoading(false)
            setIsPageLoading(false)
            return dispatch(raiseToast({severity:'error', summary: "Seller Debit Note", detail: "Seller Debit Note was not found.", life: 3000}))
        }

        downloadFile(docCopyRes.docLocation, "sellerDebitNote.pdf")
        setIsPageLoading(false)
        setIsPDFLoading(false)
    }

    const handleCancelAdjustmentNote = async () => {

        const stockMovetypeIds = SDNStockMoves.map(eachStockMove => eachStockMove.stockMoveTypeId)
        const isSDNFromSmartStock = stockMovetypeIds.includes(41)

        if(adjustmentNote.linkedDocNo){
            return dispatch(raiseToast({severity: 'error', detail: `This is seller debit note is generated against credit note ${adjustmentNote.linkedDocNo}, Please cancel the buyer credit note to cancel this seller debit note!`, life: 3000}))
        }

        const isServiceHSNsProductsExists = adjustmentNoteItems.some(eachAdjustmentNoteItem => {
            const relatedSellerPIPunchItem = sellerPIPunchItems.find(eachSellerPIPunchItem => eachSellerPIPunchItem.sellerPIPunchItemId === eachAdjustmentNoteItem.sellerPIPunchItemId)
            const relatedSellerPOItem = sellerPOItems.find(eachSellerPOItem => eachSellerPOItem.sellerPOItemId === relatedSellerPIPunchItem.sellerPOItemId)
            if(relatedSellerPOItem.HSNCode.toString().slice(0, 2) === '99' && relatedSellerPOItem.HSNCode.toString().length === 6){
                return true
            }
            return false
        })

        if(isServiceHSNsProductsExists){
            //return dispatch(raiseToast({severity:'error', detail:`Debit Note shouldn't be cancelled as it contains service HSN products.`}))
        }

        let confitmationText = 'Are you sure you want to continue?'
        if(!isSDNFromSmartStock){
            confitmationText = `Cancelling seller debit note will add materials to smart stock. Can't use the materials in this requirement anymore. Are you sure you want to continue?`
        }

        const accept = async () => {
            setIsPageLoading(true)
            const updatedAdjustmentNoteRes = await cancelAdjustmentNotes({adjustmentNoteId})

            if(updatedAdjustmentNoteRes?.failure){
                dispatch(raiseToast({severity:'error', summary: 'Cancel Debit Note', detail: updatedAdjustmentNoteRes.data.error, life: 3000}))
            }
            else{
                dispatch(raiseToast({severity:'warn', detail: "Debit Note cancelled successfully!", life: 3000}))
                getAdjustmentDetails()
            }
            setIsPageLoading(false)
        }

        const reject = () => {
            //Nothing happens for now
        }

        confirmDialog({
            message: confitmationText,
            header: 'Cancel Seller Debit Note',
            icon: 'pi pi-exclamation-triangle',
            rejectLabel: "No",
            acceptLabel: "Yes",
            accept,
            reject
        })
    }

    const fileUploadHandler = ({ files, options }, doc_name) => {
        const [file] = files;
        const fileReader = new FileReader();
		if(file.size > 5242880){
			options.clear()
			return dispatch(raiseToast({severity:'error', summary: "Upload File", detail: 'File size exceeds 5MB', life: 3000}))
		}
        console.log("doc", doc_name)
        fileReader.onload = (e) => {
            uploadExpenseRelatedCertificates(doc_name, file.name, e.target.result,options.props.name);
        }
        fileReader.readAsDataURL(file);
		
        options.clear()
    }

	const uploadExpenseRelatedCertificates = async (doc_name, file_name, data_uri, input_name) => {
        let data = {
            doc_name,
            file_name,
            data_uri
        }
        let response = await uploadFile(data)

        console.log("upload-file-response", response)
        if (response) {
            setCreditNoteDocFile(response.data)
        } else{
			console.log("error")
		}
    }

	const removeExpenseRelatedCertificates = (event, certificateName, docString) => {
        event.stopPropagation();
        setCreditNoteDocFile(null)
    }

    const handleApproveAdjustmentNote = async () => {

        if (window.confirm("Are you sure you want to continue...?")){
            setIsPageLoading(true)
            const approveAdjustmentNoteRes = await approveAdjustmentNote(adjustmentNoteId)
            
            if(approveAdjustmentNoteRes?.failure){
                dispatch(raiseToast({severity:'error', detail: approveAdjustmentNoteRes.data.error}))
            }else{
                dispatch(raiseToast({severity:'success', detail: "Seller debit note approved successfully!"}))
                getAdjustmentDetails()
            }
            setIsPageLoading(false)
        }
    }

    const renderHTMLInvoiceDocument = () => {
        console.log("HTML Trigger")
        const sanitizedHTML = DOMPurify.sanitize(sellerDebitNoteHTML)

        return(
            <div className="overflow-y-scroll md:overflow-y-hidden">
                {Parser(sanitizedHTML)}
            </div>
        )
    }

    const renderAdjustmentNotePDF = (adjustmentNotePDFURL) => {
        console.log("PDF trigger")
        return(
            <div className="document-y-scroll">
                <div className="" style={{width: '210mm'}}>
                    <DocumentView fileUrl={adjustmentNotePDFURL} />
                </div>
            </div>
            
        )
    }

    const handleCreditNoteDoc = () => {
        if (adjustmentNote?.docProof){
            window.open(adjustmentNote?.docProof)
        }else{
            setIsUploadFileVisible(true)
        }
    }

    const footerContent = () => {
        return(
            <div>
                <Button label="Cancel" severity="danger" outlined onClick={() => {setIsUploadFileVisible(false) ; setCreditNoteDocFile(null)}}/>
                <Button label="Submit" severity="" outlined onClick={() => handleDocumentSubmit()}/>
            </div>
        )
    }

    const handleDocumentSubmit = async () => {
        if(!creditNoteNo.trim()){
            return dispatch(raiseToast({severity:'error', detail: 'Please enter credit note no', life: 3000}))
        }
        if(!creditNoteDocFile){
            return dispatch(raiseToast({severity:'error', detail: 'Please upload document', life: 3000}))
        }
        if (window.confirm("Are you sure you want to continue...?")){
            setIsPageLoading(true)
            const updateDebitNoteRes = await updateAdjustmentNote(adjustmentNoteId, {docProof: creditNoteDocFile, docProofNo: creditNoteNo.trim(), docProofUploadedDate: new Date()})
            if (updateDebitNoteRes?.failure){
                dispatch(raiseToast({severity:'error', summary: "Upload File", detail: updateDebitNoteRes?.data?.error, life: 3000}))
            }else{
                dispatch(raiseToast({severity:'success', summary: "Upload File", detail: 'File has been Uploaded', life: 3000}))
                setIsUploadFileVisible(false)
                setCreditNoteDocFile(null)
                getAdjustmentDetails()
            }
            setIsPageLoading(false)
        }
    }

    return (
        <>
            <div style={{ }} className="pb-3 document-page document-page-height debit-note-document-page-container">
                <div className="document-page-container">
                    <div className="flex justify-content-end">
                        <Menu model={actionMenuItems} popup menuRef={actionMenu} />
                        <Button className="h-2rem mb-2 p-2 action-btn hover:surface-400 hover:text-700 surface-300" label="Action" onClick={(e) => actionMenu.current.toggle(e)} />
                    </div>
                </div>
                {isLoading ? (
                    <div className="flex justify-content-center align-items-center">
                        <ThreeDots height="50" width="50" radius="5" color="#00baac" ariaLabel="three-dots-loading" visible={true} />
                    </div>
                ) : (
                    adjustmentNotePDFURL ? (
                        renderAdjustmentNotePDF(adjustmentNotePDFURL)
                    ) : (
                        <div className={adjustmentNote?.statusId === 6 && "cancelled-watermark"}>
                            {renderHTMLInvoiceDocument()}
                        </div>
                    )
                )}
                <Loading visible={isPageLoading}/>
                <Dialog className="send-email-modal" header="Upload Credit Note Doc" visible={isUploadFileVisible} footer={footerContent} style={{ width: '40vw',minHeight:"40%" }} onHide={() => { setIsUploadFileVisible(false) ; setCreditNoteDocFile(null)}}>
                    <div className="col-4">
                        <TextInputField name="docProofNo" labelText="Credit Note No" value={creditNoteNo} onChange={(event) => setCreditNoteNo(event.target.value)} />
                    </div>
                    <div className="grid">
                        <div className="mt-4">
                            <FileUpload mode="basic" name="creditNoteDoc" accept="image/pdf/*"  customUpload uploadHandler={(files) => fileUploadHandler(files, "CREDIT_NOTE_DOC")} auto chooseLabel="Upload Credit Note Doc" />
                        </div>
                        {
                            creditNoteDocFile && 
                            <li className="vendor-details-li flex flex-wrap mt-3 ml-3">
                                <a href={creditNoteDocFile} className="view-btn mr-3" target="_blank">View</a>
                                <a style={{cursor : "pointer"}} className="vendor-component-remove-btn" name="remove_invoice" onClick={(event) => removeExpenseRelatedCertificates(event, "creditNoteDoc", creditNoteDocFile)} >Remove</a>
                            </li>
                        }
                    </div>
                </Dialog>
            </div>
        </>
    )
}

export default DebitNote;